import { useState } from "react";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PROTESE } from "@/lib/ajuda/dialogos/protese";
import { FilesDropzone } from "@/components/files/FilesDropzone";

export type ReworkTarget = {
  serviceId: string;
  serviceLabel: string;
  patientName: string;
} | null;

/** Quem está abrindo. Muda só a linguagem — o efeito no servidor é o mesmo evento. */
type Lado = "clinica" | "laboratorio";

type Anexo = { fileId: string; name: string };

/** Modal de retrabalho — observação obrigatória + anexos (reaproveita o módulo de Arquivos
 *  genérico). Os arquivos sobem na hora, mas só ficam ligados ao serviço quando a solicitação é
 *  enviada; desistir apaga o que subiu. Ao confirmar, o serviço reabre pra "Em produção" e o OUTRO
 *  lado é avisado — retrabalho é um evento (`ProsthesisReworkRequest`), não um status — ver
 *  `prosthesis.requestRework`.
 *
 *  O mesmo diálogo serve às duas pontas. Do lado do laboratório não é "solicitar" nada: ele é quem
 *  vai refazer, então o texto fala de reabrir o serviço, não de pedir. */
export function RequestReworkDialog({
  target,
  lado = "clinica",
  onClose,
}: {
  target: ReworkTarget;
  lado?: Lado;
  onClose: () => void;
}) {
  const ehLab = lado === "laboratorio";
  const utils = trpc.useUtils();
  const [notes, setNotes] = useState("");
  const [anexos, setAnexos] = useState<Anexo[]>([]);

  const attachToEntity = trpc.file.attachToEntity.useMutation();
  const apagarArquivo = trpc.file.delete.useMutation();

  function limpar() {
    setNotes("");
    setAnexos([]);
    onClose();
  }

  /** Desistiu: o que subiu não fica solto no serviço nem no armazenamento. */
  function handleClose() {
    for (const a of anexos) apagarArquivo.mutate({ fileId: a.fileId });
    limpar();
  }

  const requestRework = trpc.prosthesis.requestRework.useMutation({
    onSuccess: async () => {
      if (target && anexos.length) {
        const r = await Promise.allSettled(
          anexos.map((a) => attachToEntity.mutateAsync({ fileId: a.fileId, relatedType: "PROSTHESIS_SERVICE", relatedId: target.serviceId })),
        );
        if (r.some((x) => x.status === "rejected")) toast.error("Algum arquivo não foi anexado. Anexe de novo pela aba Arquivos do serviço.");
      }
      toast.success(ehLab ? "Serviço reaberto pra refazer." : "Retrabalho solicitado ao laboratório.");
      // status muda (volta pra EM_PRODUCAO) — invalida os dois lados + contadores do dashboard.
      utils.prosthesis.list.invalidate();
      utils.prosthesis.getById.invalidate();
      utils.prosthesis.countByStatus.invalidate();
      utils.prosthesis.incoming.list.invalidate();
      utils.prosthesis.incoming.getById.invalidate();
      utils.file.invalidate();
      limpar();
    },
    onError: (e) => toast.error(e.message || "Erro ao solicitar retrabalho."),
  });

  const valido = notes.trim().length >= 3;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!target || !valido) return;
    requestRework.mutate({ serviceId: target.serviceId, notes: notes.trim() });
  }

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && !requestRework.isPending && handleClose()}>
      <DialogContent className="max-w-lg dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={ehLab ? AJUDA_PROTESE.refazerServico : AJUDA_PROTESE.solicitarRetrabalho}>
            {ehLab ? "Refazer serviço" : "Solicitar retrabalho"}
          </DialogTitle>
          <DialogDescription>
            {target && `${target.patientName} · ${target.serviceLabel}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">
              {ehLab ? "O que precisa ser refeito? *" : "O que precisa ser ajustado? *"}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              autoFocus
              placeholder={
                ehLab
                  ? "Descreva o motivo — a clínica é avisada de que o serviço voltou pra produção..."
                  : "Descreva o motivo do retrabalho e o que o laboratório precisa corrigir..."
              }
              className="w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">
              Fotos, escaneamentos (STL/PLY) ou exames
            </label>
            {target && <FilesDropzone kind="GENERIC" onUploaded={(file) => setAnexos((a) => [...a, file])} />}
            {anexos.length > 0 && (
              <ul className="mt-2 space-y-1">
                {anexos.map((a) => (
                  <li key={a.fileId} className="flex items-center gap-2 rounded-md bg-zinc-50 px-2.5 py-1.5 text-xs text-zinc-700 dark:bg-muted/40 dark:text-foreground/80">
                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                    <span className="min-w-0 flex-1 truncate">{a.name}</span>
                    <button
                      type="button"
                      aria-label={`Tirar ${a.name}`}
                      onClick={() => {
                        apagarArquivo.mutate({ fileId: a.fileId });
                        setAnexos((lista) => lista.filter((x) => x.fileId !== a.fileId));
                      }}
                      className="rounded p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-muted"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
            {!valido && <span className="mr-auto text-[11.5px] text-zinc-500 dark:text-muted-foreground">Descreva o que precisa ser ajustado.</span>}
            <button
              type="button"
              onClick={handleClose}
              disabled={requestRework.isPending}
              className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:text-foreground dark:hover:bg-muted"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={requestRework.isPending || !valido}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {requestRework.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {ehLab ? "Reabrir serviço" : "Enviar solicitação"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
